/**
 * Interstitial ads.
 *
 * ❓ PLACEHOLDER — the whole policy below is a **product decision** (spec of
 * 2026-09-04), not something read from the game. The shipped
 * `AdService.ShouldShowInterstitial` (`__0x20d6e84.c`) only asks the SDK:
 *
 *     if (!enabled)                                            return false
 *     if (!RLAdvertisementManager.isInterstitialReady())        return false
 *     if (RLAdvertisementManager.ShouldShowInterstitial() == 0) return true
 *     else                                                     return false
 *
 * Min-level, capping and the dice roll live *behind* that last call, inside the
 * Rollic SDK and its remote config — so none of the three constants here can be
 * given an RVA. See `web/docs/ADS-MONETIZATION.md` §A.3, and the "còn đoán"
 * table in `GAMEPLAY-CHECKLIST.md` §10.
 *
 * One shape worth keeping from `AdService.OnInterstitialAdRequestReceived`
 * (`__0x20d6db4.c`): when the ad is refused it invokes the request's `OnAdClosed`
 * **immediately**, so the game moves on. `maybeInterstitial` never rejects, for
 * the same reason — every call site is `await` and then load the level.
 */

import { nativeBridge, type NativeBridge } from './bridge'
import { monetizationAnalytics, type MonetizationAnalytics } from './analytics'

/** ❓ Product decision. Spec: level 10, 90 s cap, 50 % roll. */
export const INTERSTITIAL_POLICY = {
  minLevel: 10,
  capSeconds: 90,
  chance: 0.5,
} as const

export interface InterstitialState {
  /** The level the player is on when the button is pressed. */
  level: number
  /** When an ad was last *shown*, not last attempted. 0 = never. */
  lastShownAt: number
  adsRemoved: boolean
}

export interface InterstitialPolicy {
  minLevel: number
  capSeconds: number
  chance: number
}

export type InterstitialSkip = 'removed' | 'level' | 'cap' | 'dice'

export type InterstitialDecision = { show: true } | { show: false; reason: InterstitialSkip }

/**
 * The three gates, in the order the spec words them: level, then cap, then dice.
 *
 * `now` and `roll` are parameters rather than `Date.now()` / `Math.random()`
 * reads so the gate stays deterministic under test (CLAUDE.md §4). The caller
 * injects the real ones.
 *
 * The dice roll comes **after** the cap on purpose: passing the cap is a
 * necessary condition, not a sufficient one. Spec, verbatim: "random 50% chứ
 * không phải hết 120s là 100% xuất hiện inter".
 */
export function interstitialGate(
  state: InterstitialState,
  now: number,
  roll: number,
  policy: InterstitialPolicy = INTERSTITIAL_POLICY,
): InterstitialDecision {
  // Not in DiceFlow, which trusts the app to drop the ad. Cheap, and it makes
  // the entitlement testable without a native host. See docs §B.3.
  if (state.adsRemoved) return { show: false, reason: 'removed' }
  if (state.level < policy.minLevel) return { show: false, reason: 'level' }
  if (now - state.lastShownAt < policy.capSeconds * 1000) return { show: false, reason: 'cap' }
  if (roll >= policy.chance) return { show: false, reason: 'dice' }
  return { show: true }
}

/** `?adsminlevel=1&adscap=0&adschance=1` — override one constant for QA. */
function overrideNumber(name: string): number | undefined {
  const raw = new URLSearchParams(location.search).get(name)
  if (raw === null || raw === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

function readPolicy(): InterstitialPolicy {
  const forced = (window as { __adsForce?: Partial<InterstitialPolicy> }).__adsForce ?? {}
  return {
    minLevel: overrideNumber('adsminlevel') ?? forced.minLevel ?? INTERSTITIAL_POLICY.minLevel,
    capSeconds: overrideNumber('adscap') ?? forced.capSeconds ?? INTERSTITIAL_POLICY.capSeconds,
    chance: overrideNumber('adschance') ?? forced.chance ?? INTERSTITIAL_POLICY.chance,
  }
}

function readDebug(): boolean {
  if (new URLSearchParams(location.search).get('adsdebug') === '1') return true
  try {
    return localStorage.getItem('ads_debug') === '1'
  } catch {
    return false
  }
}

const SKIP_MESSAGE: Record<InterstitialSkip, string> = {
  removed: 'ads removed',
  level: 'below min level',
  cap: 'inside cap',
  dice: 'lost the roll',
}

export class AdService {
  private lastShownAt = 0
  private pending: Promise<boolean> | null = null
  private readonly policy = readPolicy()
  private readonly debug = readDebug()

  constructor(
    private readonly bridge: NativeBridge = nativeBridge,
    private readonly analytics: MonetizationAnalytics = monetizationAnalytics,
    private readonly now: () => number = Date.now,
    private readonly roll: () => number = Math.random,
  ) {
    if (this.debug) {
      this.log(
        `config · minLevel=${this.policy.minLevel} cap=${this.policy.capSeconds}s chance=${this.policy.chance}`,
      )
    }
  }

  /** Set from the store entitlement; blocks the gate outright. */
  adsRemoved = false

  /**
   * Show an interstitial if the policy allows one, then resolve — **always**.
   * Never rejects and never throws, so a call site can be
   * `await ads.maybeInterstitial(level)` followed by the level load.
   */
  async maybeInterstitial(level: number): Promise<void> {
    const decision = interstitialGate(
      { level, lastShownAt: this.lastShownAt, adsRemoved: this.adsRemoved },
      this.now(),
      this.roll(),
      this.policy,
    )
    if (!decision.show) {
      this.log(`inter skip · ${SKIP_MESSAGE[decision.reason]} (level ${level})`)
      return
    }

    this.analytics.log('interstitial_request', level)
    if (await this.show()) {
      // Only a *shown* ad spends the cap. Losing the roll must not, or the ad
      // density collapses well below what the spec asks for.
      this.lastShownAt = this.now()
      this.log('inter SHOWN')
      this.analytics.log('interstitial_shown', level)
    } else {
      // Not an error: entitlement, app-side cooldown, no fill, or no wrapper.
      this.log('inter no-fill · app returned shown:false')
      this.analytics.log('interstitial_no_fill', level)
    }
  }

  /** One pending ad at a time — a double tap must not open two. */
  private show(): Promise<boolean> {
    if (this.pending) return this.pending
    this.pending = this.bridge
      // A plain browser cannot show an ad, so offline is `shown: false` — this
      // is why `npm run dev` never displays one. Deliberate; see docs §A.2.1.
      .request('show_interstitial', 'interstitial', { force: true }, { shown: false })
      .then((reply) => reply.shown === true)
      .finally(() => {
        this.pending = null
      })
    return this.pending
  }

  private log(message: string): void {
    if (this.debug) console.log('[ADS] ' + message)
  }
}
